import React, { useEffect, useState } from 'react';
import { Box, Flex, Text } from 'rebass/styled-components';
import styled from 'styled-components';
import { useLocation, Navigate, Outlet, useNavigate } from 'react-router-dom';
import withAnalytics, { IWithAnalyticsReturn } from '../lib/withAnalytics';
import { useHasChanged } from '../hooks/lifecycle/useHasChanged';
import { IUser } from '../graphql/types/queries/user';
import { useQuery } from '@apollo/client';
import withSession from '../lib/withSession';
import HamburgerMenu from '../components/HamburgerMenu';
import { Constants } from '../utils/Constants';
import { OpenedConversationsContext, RefreshOpenedConversationsContext, UnreadInxsContext } from '../contexts/openConversationsCounts';
import { IGetOpenedConversationsCountInput, IGetOpenedConversationsCountOutput } from '../graphql/types/queries/conversation';
import { GET_OPEN_CONVERSATION_COUNT, GET_UNREAD_INXS } from '../graphql/queries';
import { toast } from 'react-toastify';
import HamburgerMenuMobile from '../components/HamburgerMenuMobile';
import { IGetUnreadInxsInput, IGetUnreadInxsOutput } from '../graphql/types/queries/inxs';
import { PasswordChange } from './PasswordChange';
import NavigateOverlayBox from '../components/NavigateOverlayBox';

const Container = styled(Box)`
  display: flex;
  flex-direction: column;
  height: 100% !important;
  max-height: 100% !important;
`;

const BottomNavBarWrap = styled(Flex)`
  overflow: hidden;
  position: fixed;
  bottom: 0;
  width: 100%;
  /* height: 50px; */
  padding-left: 4px;
  padding-right: 4px;
  padding-bottom: 4px;
  z-index: 10000;
  @media screen and (min-width: 68.8125em) {
    display: none;
  }
`

const ImpersonateBanner = styled(Flex)`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 32px;
  background: #f6a293;
  opacity: 0.5;
  z-index: 1000;
  justify-content: center;
  align-items: center;
  transition: all 0.3s ease;

  &:hover {
    opacity: 1;
  }
`

const OutletContainer = styled(Box) <{ marginLeft: number | string }>`
  margin-left: 0;
  /* padding-bottom: 100px; */
  @media screen and (min-width: 68.8125em) {
    margin-left: ${props => props.marginLeft};
    /* padding-bottom: initial; */
  }
`

interface IDashboardProps {
  analytics: IWithAnalyticsReturn,
  session: IUser
}

const FULL_SCREEN_PAGES = [
  '/complete-profile'
]

const SignedIn: React.FC<IDashboardProps> = ({ analytics, session }) => {
  const location = useLocation();
  const navigate = useNavigate()
  const pathChanged = useHasChanged(location.pathname)
  const [isMinified, setIsMinified] = useState(false)
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [unreadInxsCount, setUnreadInxsCount] = useState<number>(0);
  const { data, loading, refetch, client } = useQuery<IGetOpenedConversationsCountOutput, IGetOpenedConversationsCountInput>(GET_OPEN_CONVERSATION_COUNT, {
    variables: {
      status: 'open'
    },
    pollInterval: 30000,
    notifyOnNetworkStatusChange: true,
    onCompleted: (d) => {
      if (!d || !d.getHealthCoachConversationsOpenedCount || !d.getHealthCoachConversationsOpenedCount.count) {
        setUnreadCount(0)
        return
      }
      setUnreadCount(d.getHealthCoachConversationsOpenedCount?.count)
      // openedConversationsContext.openedConversationscount = d.getProviderConversations.page.totalElements
    }
  })
  const { data: dataInxsUnread, loading: loadingInxsUnread, refetch: refetchInxsUnread } = useQuery<IGetUnreadInxsOutput, IGetUnreadInxsInput>(GET_UNREAD_INXS, {
    pollInterval: 60000,
    notifyOnNetworkStatusChange: true,
    onCompleted: (d) => {
      if (!d || !d.getHealthCoachUnreadInxsCount || !d.getHealthCoachUnreadInxsCount.count) {
        setUnreadInxsCount(0)
        return
      }
      setUnreadInxsCount(d.getHealthCoachUnreadInxsCount?.count)
      // openedConversationsContext.openedConversationscount = d.getProviderConversations.page.totalElements
    }
  })

  useEffect(() => {
    if (location.pathname.indexOf('purchase') < 0) {
      analytics.page();
    }
  }, [])

  useEffect(() => {
    window.scroll(0, 0);
    if (location.pathname.indexOf('purchase') < 0) {
      analytics.page();
    }

  }, [pathChanged])

  const exitImpersonationMode = async () => {
    toast.success(<Flex flexDirection={'column'}>
      <Text fontWeight={400}>Exit Impersonation</Text>
    </Flex>, {
      autoClose: 5000,
      position: toast.POSITION.TOP_RIGHT,
    })
    const initialToken = localStorage.getItem('initialToken');

    // console.log('Initial Tok', initialToken);
    localStorage.setItem('token', initialToken || '');
    localStorage.setItem('initialToken', '');
    await client.clearStore()
    await client.cache.reset()
    navigate('/impersonate')
  }
  const initTok = localStorage.getItem('initialToken');


  if (session && session.passwordExpired) {
    return (
      <OpenedConversationsContext.Provider value={unreadCount}>
        <UnreadInxsContext.Provider value={unreadInxsCount}>
          <RefreshOpenedConversationsContext.Provider value={refetch}>
            <Container id="cont" style={{ flex: 1, flexDirection: 'row' }}>
              <PasswordChange />
            </Container>
          </RefreshOpenedConversationsContext.Provider>
        </UnreadInxsContext.Provider>
      </OpenedConversationsContext.Provider>)
  }

  return (
    <OpenedConversationsContext.Provider value={unreadCount}>
      <UnreadInxsContext.Provider value={unreadInxsCount}>
        <RefreshOpenedConversationsContext.Provider value={refetch}>
          <Container id="cont" style={{ flex: 1, flexDirection: 'row', position:'relative' }}>
            {FULL_SCREEN_PAGES.indexOf(location.pathname) < 0 && (
              <HamburgerMenu isMinified={isMinified} setIsMinified={setIsMinified} />
            )}
            <OutletContainer
              marginLeft={0}//{FULL_SCREEN_PAGES.indexOf(location.pathname) < 0 ? isMinified ? `${Constants.HAMBURGER_MINIFIED_WIDTH}px` : `${Constants.HAMBURGER_EXTENDED_WIDTH}px` : 0} 
              style={{ flex: 1, overflowX: 'hidden' }}>
              <Outlet />
            </OutletContainer>
            {!!initTok && (
              <ImpersonateBanner display={['none!important', 'flex !important', 'flex !important']} ml={FULL_SCREEN_PAGES.indexOf(location.pathname) < 0 ? isMinified ? `${Constants.HAMBURGER_MINIFIED_WIDTH}px` : `${Constants.HAMBURGER_EXTENDED_WIDTH}px` : 0}>
                <div
                  style={{
                    color: 'white',
                    fontWeight: '500',
                    textAlign: 'center',
                  }}
                >
                  Impersonating Mode Active
                  {' - '}
                  <span style={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={() => exitImpersonationMode()}>
                    Exit
                  </span>
                </div>
              </ImpersonateBanner>
            )}
            <BottomNavBarWrap>
              <HamburgerMenuMobile />
            </BottomNavBarWrap>
            <NavigateOverlayBox />
          </Container>
        </RefreshOpenedConversationsContext.Provider>
      </UnreadInxsContext.Provider>
    </OpenedConversationsContext.Provider>
  )
}

const StandardRoutes = ({ component: Component, session, ...rest }: { component: React.FC<any>, session: IUser }) => {
  const location = useLocation()
  const token = localStorage.getItem('token');
  var images = document.getElementsByTagName('img');
  if (location && location.pathname && location.pathname !== '/login' && location.pathname !== '/logout' &&
    (location.pathname || '').indexOf('/reset-password') < 0 &&
    location.pathname !== '/forgot-password' &&
    (location.pathname || '').indexOf('/unlock/') < 0 &&
    location.pathname !== '/account-locked') {
    localStorage.setItem('route', location.pathname)
  };
  for (let index = 0; index < images.length; index++) {
    const element = images[index];

    if (element.currentSrc && element.currentSrc.indexOf('https://s.amazon-adsystem') > -1) {
      element.style.position = 'absolute'
      element.style.top = '0'
    }
  }
  if (!!token) {
    document.body.style.backgroundColor = '#F2F5F9';
    if (!!session.mustCompleteRegistration) {
      return <Navigate to={{ pathname: '/complete-profile' }} />;
    }
    return <Component {...rest} />;
  } else {
    document.body.style.backgroundColor = '#FDF5F2';
    return <Navigate to={{ pathname: '/logout' }} />;
  }
};
export const ImpersonateRoute = ({ component: Component, ...rest }: { component: React.FC<any> }) => {
  const token = localStorage.getItem('token');
  var images = document.getElementsByTagName('img');

  for (let index = 0; index < images.length; index++) {
    const element = images[index];

    if (element.currentSrc && element.currentSrc.indexOf('https://s.amazon-adsystem') > -1) {
      element.style.position = 'absolute'
      element.style.top = '0'
    }
  }
  if (!!token) {
    document.body.style.backgroundColor = '#F2F5F9';

    return <Component {...rest} />;
  } else {
    document.body.style.backgroundColor = '#FDF5F2';
    return <Navigate to={{ pathname: '/logout' }} />;
  }
}
export const StandardRoute = withSession(StandardRoutes)
export const MustCompleteRoutes = ({ component: Component, session, ...rest }: { component: React.FC<any>, session: IUser }) => {
  const token = localStorage.getItem('token');
  var images = document.getElementsByTagName('img');
  for (let index = 0; index < images.length; index++) {
    const element = images[index];

    if (element.currentSrc && element.currentSrc.indexOf('https://s.amazon-adsystem') > -1) {
      element.style.position = 'absolute'
      element.style.top = '0'
    }
  }
  if (!!token) {

    if (!session || !session.mustCompleteRegistration) {
      return <Navigate to={{ pathname: '/' }} />;
    }
    document.body.style.backgroundColor = '#F2F5F9';
    return <Component {...rest} />;
  } else {
    document.body.style.backgroundColor = '#FDF5F2';
    return <Navigate to={{ pathname: '/logout' }} />;
  }
};
export const MustCompleteRoute = withSession(MustCompleteRoutes)

export default withSession(withAnalytics(SignedIn))